import { ButtonGroup, Flex, Stack, Text } from "@chakra-ui/react";
import type { TFunction } from "i18next";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { SavingsMeasure, type ContractSavingsDto, type LargestSavingDto } from "../../autogen/bff-api";
import { FilterChip } from "../../common/components/FilterChip";
import { urls } from "../../urls";
import { OverviewBar } from "./OverviewBar";
import { MEASURE_PARAM, formatMoney, formatShare, parseSavingsMeasure } from "./savingsFigures";

const MEASURES: SavingsMeasure[] = [SavingsMeasure.EstimatedValue, SavingsMeasure.FirstBid];

export const LargestSavings = ({ savings }: { savings: ContractSavingsDto }) => {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const measure = parseSavingsMeasure(searchParams.get(MEASURE_PARAM));
  const chosen = savings.largest.find((largest) => largest.measure === measure);
  const contracts = chosen?.contracts ?? [];
  const largest = Math.max(1, ...contracts.map((contract) => contract.saved));
  const choose = (next: SavingsMeasure) =>
    setSearchParams(
      (previous) => {
        const params = new URLSearchParams(previous);
        params.set(MEASURE_PARAM, next);
        return params;
      },
      { replace: true },
    );

  return (
    <Stack
      spacing="3"
      borderWidth="1px"
      borderColor="smBorder"
      borderRadius="lg"
      p="4"
      bg="smBackground"
      data-testid="largest-savings"
    >
      <Text as="h2" fontWeight="semibold" color="smText">
        {t("Largest savings")}
      </Text>
      <Flex gap="2" align="center" wrap="wrap">
        <Text fontSize="sm" color="smMuted">
          {t("Measured against:")}
        </Text>
        <ButtonGroup size="sm" spacing="0" gap="2" flexWrap="wrap">
          {MEASURES.map((choice) => (
            <FilterChip
              key={choice}
              active={measure === choice}
              label={measureLabel(t, choice)}
              onClick={() => choose(choice)}
            />
          ))}
        </ButtonGroup>
      </Flex>
      {contracts.length === 0 ? (
        <Text fontSize="sm" color="smMuted" data-testid="largest-savings-empty">
          {emptyText(t, measure, (chosen?.measured ?? 0) > 0)}
        </Text>
      ) : (
        <Stack spacing="1">
          {contracts.map((contract, index) => (
            <OverviewBar
              key={contract.contractId}
              label={contract.title || String(t("Untitled"))}
              width={(contract.saved / largest) * 100}
              shown={formatMoney(contract.saved, savings.currency)}
              details={details(t, measure, contract, savings.currency)}
              link={urls.app.contracts.view.go(contract.contractId)}
              linkLabel={String(t("Open the contract"))}
              testId={`largest-${index}`}
            />
          ))}
        </Stack>
      )}
    </Stack>
  );
};

const measureLabel = (t: TFunction, measure: SavingsMeasure): string =>
  measure === SavingsMeasure.FirstBid ? t("First bid") : t("Estimated value");

const emptyText = (t: TFunction, measure: SavingsMeasure, anyMeasured: boolean): string => {
  if (measure === SavingsMeasure.FirstBid) {
    return anyMeasured
      ? t("No contracts in the period have a saving measured against the first bid")
      : t("No contract in the period has a first bid to measure against");
  }
  return anyMeasured
    ? t("No contracts in the period have a saving measured against estimated value")
    : t("No contract in the period has an estimated value to measure against");
};

const details = (t: TFunction, measure: SavingsMeasure, contract: LargestSavingDto, currency: string): string[] => {
  const baseline = formatMoney(contract.baseline, currency);
  const awarded = formatMoney(contract.awarded, currency);
  const saved = formatMoney(contract.saved, currency);
  return [
    ...(contract.supplierName ? [t("Supplier: {{name}}", { name: contract.supplierName })] : []),
    measure === SavingsMeasure.FirstBid
      ? t("First bid: {{value}}", { value: baseline })
      : t("Estimated value: {{value}}", { value: baseline }),
    measure === SavingsMeasure.FirstBid
      ? t("Last round: {{value}}", { value: awarded })
      : t("Value at award: {{value}}", { value: awarded }),
    contract.share == null
      ? t("Saving: {{value}}", { value: saved })
      : t("Saving: {{value}} ({{share}})", { value: saved, share: formatShare(contract.share) }),
    ...(contract.contractsFromProcurement > 1
      ? [t("The procurement gave {{count}} contracts. Its saving counts once.", { count: contract.contractsFromProcurement })]
      : []),
  ];
};
